<template>
  <div class="ReviewCarouselTabs">
    <div class="ReviewCarouselTabs__viewport">
      <transition name="ReviewCarouselTabs__fade" mode="out-in">
        <ReviewCard
          v-if="currentReview"
          :key="activeIndex"
          :id="`review-panel-${activeIndex}`"
          class="ReviewCarouselTabs__card"
          :rating="currentReview.rating"
          :name="currentReview.name"
          :jobTitle="currentReview.jobTitle"
          :companyLogoKey="currentReview.companyLogoKey"
          :logoHeight="currentReview.logoHeight"
        >
          <template #text>
            {{ currentReview.text }}
          </template>
        </ReviewCard>
      </transition>
    </div>

    <div
      v-if="reviewsList.length > 1"
      class="ReviewCarouselTabs__controls"
      aria-label="Review navigation"
    >
      <button
        class="ReviewCarouselTabs__nav"
        type="button"
        :disabled="isAtStart"
        aria-label="Previous review"
        @click="goPrev"
      >
        <UIIcon name="arrowLeft" />
      </button>
      <button
        class="ReviewCarouselTabs__nav"
        type="button"
        :disabled="isAtEnd"
        aria-label="Next review"
        @click="goNext"
      >
        <UIIcon name="arrowRight" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, PropType, ref, watch } from 'vue'
import ReviewCard from '@/views/components/cards/ReviewCard.vue'
import UIIcon from '@/views/components/ui/icons/UIIcon.vue'
import {
  type ReviewCarouselItem,
  REVIEW_CAROUSEL_DEFAULTS,
} from './reviewCarousel.shared'

const props = defineProps({
  reviews: Array as PropType<ReviewCarouselItem[]>,
})

const reviewsList = computed(() =>
  props.reviews?.length ? props.reviews : REVIEW_CAROUSEL_DEFAULTS,
)

const activeIndex = ref(0)
const tabRefs = ref<(HTMLButtonElement | null)[]>([])

const isAtStart = computed(() => activeIndex.value === 0)
const isAtEnd = computed(
  () => activeIndex.value >= Math.max(reviewsList.value.length - 1, 0),
)

const currentReview = computed(() => reviewsList.value[activeIndex.value])

watch(
  () => reviewsList.value.length,
  (length) => {
    if (!length) {
      activeIndex.value = 0
      return
    }
    activeIndex.value = Math.min(activeIndex.value, length - 1)
  },
  { immediate: true },
)

const goPrev = () => {
  if (isAtStart.value) return
  activeIndex.value -= 1
  focusActiveTab()
}

const goNext = () => {
  if (isAtEnd.value) return
  activeIndex.value += 1
  focusActiveTab()
}

const focusActiveTab = () => {
  nextTick(() => {
    tabRefs.value[activeIndex.value]?.focus()
  })
}
</script>

<style scoped lang="scss">
.ReviewCarouselTabs {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  .ReviewCarouselTabs__tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  .ReviewCarouselTabs__tab {
    flex: 1 1 12rem;
    min-width: 10rem;
    border-radius: 999px;
    border: 1px solid $newcolor-border-light;
    background: $color-white;
    padding: 0.75rem 1rem;
    display: flex;
    flex-direction: column;
    text-align: left;
    @include font-size-14px();
    color: $color-ui-gray-cool-600;
    gap: 0.25rem;
    cursor: pointer;
    @include trans;

    &:focus-visible {
      outline: 2px solid $newcolor-primary-main;
      outline-offset: 2px;
    }

    &.ReviewCarouselTabs__tab--active {
      border-color: $newcolor-primary-main;
      background: $color-ui-brand-50;
      color: $color-ui-gray-cool-900;
    }
  }

  .ReviewCarouselTabs__tab-name {
    font-weight: 600;
  }

  .ReviewCarouselTabs__tab-role {
    color: $color-ui-gray-cool-500;
  }

  .ReviewCarouselTabs__viewport {
    width: 100%;
  }

  .ReviewCarouselTabs__card {
    width: 100%;
  }

  .ReviewCarouselTabs__controls {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    justify-content: flex-end;
    padding-bottom: 1rem;
  }

  .ReviewCarouselTabs__nav {
    @include trans;
    display: grid;
    place-items: center;
    width: 3rem;
    height: 3rem;
    border-radius: 50%;
    border: 1px solid $newcolor-border-light;
    background: $color-white;
    box-shadow: 0 2px 8px rgba(16, 24, 40, 0.08);

    &:disabled {
      opacity: 0.4;
      pointer-events: none;
    }
  }

  .ReviewCarouselTabs__counter {
    @include font-size-14px();
    color: $color-ui-gray-cool-600;
    min-width: 3rem;
    text-align: center;
  }
}

.ReviewCarouselTabs__fade-enter-active,
.ReviewCarouselTabs__fade-leave-active {
  transition: opacity 0.3s ease;
}

.ReviewCarouselTabs__fade-enter-from,
.ReviewCarouselTabs__fade-leave-to {
  opacity: 0;
}

@include md {
  .ReviewCarouselTabs {
    gap: 2rem;

    .ReviewCarouselTabs__tabs {
      flex-wrap: nowrap;
    }
  }
}
</style>
